import { useTranslation } from "react-i18next";
import { ONBOARDING_SEGMENTS } from "../../../constants";
import {
  isSalesProduct,
  PRODUCT_META,
  PRODUCTS_BY_SEGMENT,
} from "../wizard-config";
import StepHeading from "./StepHeading";

const ProductCard = ({ product, selected, onToggle, onBookACall, t }) => {
  const meta = PRODUCT_META[product];
  const sales = isSalesProduct(product);
  return (
    <div
      className={`product-card ${selected ? "selected" : ""}`}
      data-product={product}
    >
      <button
        type="button"
        className="product-card-toggle"
        aria-pressed={selected}
        aria-label={meta.name}
        onClick={() => onToggle(product)}
      />
      <div className="product-head">
        <span className={`product-initials ${product}`}>{meta.initials}</span>
        <div className="product-titles">
          <span className="product-name">{meta.name}</span>
          <span className="product-tag">
            {t(`onboardingWizard.products.${product}Tag`)}
          </span>
        </div>
        <div className="ml-auto flex gap-2 items-center">
          {meta.recommended ? (
            <span className="product-badge">
              {t("onboardingWizard.products.recommended")}
            </span>
          ) : null}
          <span className={`product-check ${selected ? "on" : ""}`} />
        </div>
      </div>
      <p className="product-desc">
        {t(`onboardingWizard.products.${product}Desc`)}
      </p>
      <div className="product-foot">
        {sales ? (
          <button
            type="button"
            className="book-a-call"
            onClick={() => onBookACall(product)}
          >
            {t("onboardingWizard.products.bookACall")}
          </button>
        ) : (
          <span className="product-meta">
            {t(`onboardingWizard.products.${product}Meta`)}
          </span>
        )}
      </div>
    </div>
  );
};

// Step 3 — products. Options depend on the segment chosen in step 2.
const StepProducts = ({
  current,
  segment,
  products,
  onToggleProduct,
  onBookACall,
}) => {
  const { t } = useTranslation("auth");
  const groups = PRODUCTS_BY_SEGMENT[segment] || { coaching: [], gym: [] };
  const isBoth = segment === ONBOARDING_SEGMENTS.BOTH;
  const isGym = segment === ONBOARDING_SEGMENTS.GYM;

  const subtitle = isBoth
    ? t("onboardingWizard.products.subtitleBoth")
    : isGym
      ? t("onboardingWizard.products.subtitleGym")
      : t("onboardingWizard.products.subtitleCoach");

  const renderCard = (product) => (
    <ProductCard
      key={product}
      product={product}
      selected={products.includes(product)}
      onToggle={onToggleProduct}
      onBookACall={onBookACall}
      t={t}
    />
  );

  const renderGroup = (labelKey, hintKey, list) =>
    list.length ? (
      <section className="product-group">
        <div className="group-head">
          <span className="group-label">
            {t(`onboardingWizard.products.${labelKey}`)}
          </span>
          <span className="group-hint">
            {t(`onboardingWizard.products.${hintKey}`)}
          </span>
        </div>
        <div className="product-grid">{list.map(renderCard)}</div>
      </section>
    ) : null;

  return (
    <div className="step-body step-products">
      <StepHeading
        current={current}
        title={t("onboardingWizard.products.title")}
        subtitle={subtitle}
      />

      {isBoth ? (
        <>
          {renderGroup(
            "coachingProductsLabel",
            "coachingProductsHint",
            groups.coaching
          )}
          {renderGroup("gymProductsLabel", "gymProductsHint", groups.gym)}
        </>
      ) : (
        // Coach-only / gym-only: a single group with no dual header.
        <div className="product-grid single">
          {[...groups.coaching, ...groups.gym].map(renderCard)}
        </div>
      )}

      {!isBoth && !isGym ? (
        <p className="cross-sell">
          {t("onboardingWizard.products.crossSellCoach")}
        </p>
      ) : null}

      <input
        type="hidden"
        name="onboardingProducts"
        value={JSON.stringify(products)}
      />
    </div>
  );
};

export default StepProducts;
